import { useEffect } from "react";

type EventForSchema = {
  _id: string;
  title: string;
  date: string;
  startTime: string;
  endTime?: string;
  venueName: string;
  venueAddress?: string;
  venueLatitude?: number;
  venueLongitude?: number;
  description?: string;
  eventUrl?: string;
  priceMin?: number;
  priceMax?: number;
  priceUnit?: string;
};

/**
 * The `offers` block, which is what puts a price in a Google event result.
 *
 * An event with no stated price gets no offer at all rather than a zero —
 * claiming free admission on an event whose organizer simply never filled the
 * field in would be a lie told at search-result scale.
 *
 * A range becomes an AggregateOffer, which is the only shape schema.org has
 * for "somewhere between these two". The per-unit part (per couple, per group)
 * has no clean equivalent, so it is left out rather than misstated.
 */
export function buildOffers(event: EventForSchema, url: string) {
  const { priceMin, priceMax } = event;
  if (priceMin === undefined && priceMax === undefined) return undefined;

  const common = {
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    url,
  };

  if (priceMin !== undefined && priceMax !== undefined && priceMax !== priceMin) {
    return {
      "@type": "AggregateOffer",
      lowPrice: priceMin,
      highPrice: priceMax,
      ...common,
    };
  }

  return {
    "@type": "Offer",
    price: priceMin ?? priceMax,
    ...common,
  };
}

function buildEventSchema(event: EventForSchema) {
  const startDate = `${event.date}T${event.startTime}:00`;
  const url = event.eventUrl ?? `https://nedances.com/events?event=${event._id}`;

  const location: Record<string, unknown> = {
    "@type": "Place",
    name: event.venueName,
  };
  if (event.venueAddress) location.address = event.venueAddress;
  if (event.venueLatitude && event.venueLongitude) {
    location.geo = {
      "@type": "GeoCoordinates",
      latitude: event.venueLatitude,
      longitude: event.venueLongitude,
    };
  }

  const schema: Record<string, unknown> = {
    "@type": "DanceEvent",
    name: event.title,
    startDate,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location,
    organizer: {
      "@type": "Organization",
      name: "NE Dances",
      url: "https://nedances.com/",
    },
    url,
  };

  if (event.endTime) schema.endDate = `${event.date}T${event.endTime}:00`;
  if (event.description) schema.description = event.description;

  const offers = buildOffers(event, url);
  if (offers) schema.offers = offers;

  return schema;
}

export function EventsJsonLd({ events }: { events: EventForSchema[] }) {
  useEffect(() => {
    if (!events.length) return;

    const schema = {
      "@context": "https://schema.org",
      "@graph": events.slice(0, 50).map(buildEventSchema),
    };

    const id = "events-jsonld";
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(schema);

    return () => {
      document.getElementById(id)?.remove();
    };
  }, [events]);

  return null;
}
